import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from '@posthog/react';
import AlertDialog from './components/AlertDialog';
import PnidFileManager from './components/PnidFileManager';
import SettingsDialog from './components/SettingsDialog';
import { useAuth } from './contexts/AuthContext';
import { documentApi } from './api';
import { rememberPlantScope } from './lib/plantScope';
import { queryKeys } from './queries/keys';
import { useAnnotations, useDocuments } from './hooks/useDocuments';
import { INITIAL_PNID_IMAGE_LEVEL, prefetchPnidImage } from './queries/usePnidImage';
import { buildPnidPath } from './utils/pnidDeepLink';
import type { AnnotationInfo, PidDocument } from './types';
import DrawingWorkspace from './pnid/DrawingWorkspace';
import WorkspaceSkeleton from './pnid/WorkspaceSkeleton';

const normalizeIdentity = (value?: string | null) =>
  (value || "").trim().toLowerCase();

/**
 * Routes between the two pages the P&ID tool has: the document picker and the
 * drawing workspace. It owns what both need — the document list, the annotation
 * index, and which drawing is open — and mounts the workspace keyed on the
 * document id so switching drawings starts the editor from clean state.
 */
const App: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { docId: routeDocId } = useParams<{ docId?: string }>();
  const posthog = usePostHog();

  const prevSelectedDocRef = useRef<PidDocument | null>(null);
  const diagramVisitCount = useRef<Record<string, number>>({});
  const [fileManagerOpen, setFileManagerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "info" | "warning";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  const showAlert = (
    title: string,
    message: string,
    type: "success" | "error" | "info" | "warning" = "info"
  ) => {
    setAlertDialog({ isOpen: true, title, message, type });
  };

  const closeAlert = () => {
    setAlertDialog((prev) => ({ ...prev, isOpen: false }));
  };

  // User state for filtering documents
  const [userName, setUserName] = useState<string>(() => {
    return localStorage.getItem("pnid_user_name") || "";
  });

  const handleSaveUser = (newUserName: string) => {
    localStorage.setItem("pnid_user_name", newUserName);
    setUserName(newUserName);
  };
  const effectiveUserName = (user?.username || userName || "").trim();

  useEffect(() => {
    if (!user?.username || userName === user.username) return;
    localStorage.setItem("pnid_user_name", user.username);
    setUserName(user.username);
  }, [user?.username, userName]);

  const isAdmin = normalizeIdentity(effectiveUserName) === "raven_admin";

  const documentsQuery = useDocuments();
  const documents = documentsQuery.data ?? [];

  const annotationsQuery = useAnnotations(isAdmin, {
    select: (list) => Object.fromEntries(list.map((a) => [a.id, a])) as Record<string, AnnotationInfo>,
  });
  const annotations = annotationsQuery.data ?? {};

  const documentQuery = useQuery({
    queryKey: queryKeys.document(routeDocId ?? ''),
    queryFn: () => documentApi.get(routeDocId!),
    enabled: Boolean(routeDocId),
    initialData: () => documents.find((d) => d.id === routeDocId),
    initialDataUpdatedAt: () => queryClient.getQueryState(queryKeys.documents())?.dataUpdatedAt,
  });
  const selectedDoc = routeDocId ? documentQuery.data ?? null : null;

  // A drawing belongs to one plant, and the rail names the app's one current
  // plant. A pasted link or a queue row can open a drawing of another plant, so
  // the open drawing decides it. A drawing names a leaf plant, which is its own
  // family; that is why no plants query is needed here.
  // The scope is shared by every tab, so a drawing opened in another tab takes
  // it. This tab takes it back when the reviewer returns to it. Only the tab in
  // view writes, so two drawing tabs never take it from each other in a loop.
  useEffect(() => {
    const domainId = selectedDoc?.domain_id;
    const plant = selectedDoc?.plant;
    if (!domainId || !plant) return undefined;
    const claimPlant = () => rememberPlantScope(domainId, plant, [plant]);
    const claimWhenVisible = () => {
      if (document.visibilityState === 'visible') claimPlant();
    };
    claimPlant();
    window.addEventListener('focus', claimPlant);
    document.addEventListener('visibilitychange', claimWhenVisible);
    return () => {
      window.removeEventListener('focus', claimPlant);
      document.removeEventListener('visibilitychange', claimWhenVisible);
    };
  }, [selectedDoc?.domain_id, selectedDoc?.plant]);

  // The canvas mounts only after the document and its annotation load. Starting
  // the sheet image here downloads it alongside them instead of after them.
  useEffect(() => {
    if (!routeDocId) return;
    void prefetchPnidImage(queryClient, routeDocId, INITIAL_PNID_IMAGE_LEVEL);
  }, [queryClient, routeDocId]);

  const reloadDocumentsAndAnnotations = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.documents() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.annotations(isAdmin) }),
    ]);
  }, [isAdmin, queryClient]);

  const openDocument = useCallback(
    (doc: PidDocument, options?: { replace?: boolean; targetNodeId?: string | null; targetEquipmentId?: string | null }) => {
      queryClient.setQueryData(queryKeys.document(doc.id), doc);
      if (routeDocId !== doc.id || options?.targetNodeId || options?.targetEquipmentId) {
        navigate(
          buildPnidPath(doc.id, {
            targetNodeId: options?.targetNodeId,
            targetEquipmentId: options?.targetEquipmentId,
          }),
          { replace: options?.replace ?? false },
        );
      }
    },
    [navigate, queryClient, routeDocId],
  );

  const closeSelectedDocument = useCallback(
    (options?: { replace?: boolean }) => {
      navigate('/', { replace: options?.replace ?? false });
    },
    [navigate],
  );

  const patchDocument = useCallback((docId: string, patch: Partial<PidDocument>) => {
    queryClient.setQueryData<PidDocument | undefined>(queryKeys.document(docId), (prev) =>
      prev ? { ...prev, ...patch } : prev,
    );
    queryClient.setQueryData<PidDocument[] | undefined>(queryKeys.documents(), (prev) =>
      prev?.map((doc) => (doc.id === docId ? { ...doc, ...patch } : doc)),
    );
  }, [queryClient]);

  const handleDocumentReady = async (docId: string) => {
    try {
      const doc = await documentApi.get(docId);
      openDocument(doc);
      await reloadDocumentsAndAnnotations();
    } catch (error) {
      console.error("Failed to load document:", error);
    }
  };

  useEffect(() => {
    // isError alone is true on a failed background refetch while previous data
    // remains — that must not kick the user out of an open drawing.
    if (!routeDocId || !documentQuery.isError || documentQuery.data) return;
    console.error("Failed to load document from URL:", documentQuery.error);
    closeSelectedDocument({ replace: true });
    showAlert(
      "PNID not found",
      "The shared PNID link could not be opened. It may have been removed or you may not have access to it.",
      "warning",
    );
  }, [closeSelectedDocument, documentQuery.data, documentQuery.error, documentQuery.isError, routeDocId]);

  // Track which user is working on which PnID diagram (for PostHog analytics)
  useEffect(() => {
    const prevDoc = prevSelectedDocRef.current;

    // Fire closed event for previous doc when switching
    if (prevDoc && prevDoc.id !== selectedDoc?.id && posthog) {
      posthog.capture('pnid_diagram_closed', {
        diagram_id: prevDoc.id,
        diagram_name: prevDoc.original_filename,
      });
    }

    if (selectedDoc && posthog) {
      const visitNum = (diagramVisitCount.current[selectedDoc.id] ?? 0) + 1;
      diagramVisitCount.current[selectedDoc.id] = visitNum;

      posthog.capture('pnid_diagram_opened', {
        diagram_id: selectedDoc.id,
        diagram_name: selectedDoc.original_filename,
        diagram_filename: selectedDoc.filename,
        user_id: user?.user_id ?? undefined,
        username: user?.username ?? undefined,
        session_sequence_number: visitNum,
        is_revisit: visitNum > 1,
      });
      posthog.register({
        diagram_id: selectedDoc.id,
        diagram_name: selectedDoc.original_filename,
      });
    } else if (posthog) {
      posthog.unregister('diagram_id');
      posthog.unregister('diagram_name');
    }

    prevSelectedDocRef.current = selectedDoc ?? null;
  }, [selectedDoc?.id, posthog, user?.user_id, user?.username]);

  return (
    <>
      {/* App only ever mounts at /pnid/:docId now, so `routeDocId` is always
          set: the picker branch that stood here served the legacy home, which
          no longer exists. */}
      {!selectedDoc ? (
        <div style={{ display: 'flex', height: '100%', minHeight: 0, backgroundColor: '#e8eaed', padding: 12 }}>
          <WorkspaceSkeleton />
        </div>
      ) : (
        <DrawingWorkspace
          key={routeDocId}
          selectedDoc={selectedDoc}
          documents={documents}
          annotations={annotations}
          effectiveUserName={effectiveUserName}
          isAdmin={isAdmin}
          onPatchDocument={patchDocument}
          onOpenDocument={openDocument}
          onReloadDocuments={reloadDocumentsAndAnnotations}
          onOpenFileManager={() => setFileManagerOpen(true)}
        />
      )}

      {/* P&ID File Manager Modal */}
      <PnidFileManager
        isOpen={fileManagerOpen}
        onClose={() => setFileManagerOpen(false)}
        onDocumentReady={handleDocumentReady}
      />

      {/* Settings Dialog */}
      <SettingsDialog
        isOpen={settingsOpen}
        currentUser={effectiveUserName}
        onClose={() => setSettingsOpen(false)}
        onSaveUser={handleSaveUser}
      />

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={closeAlert}
      />
    </>
  );
};

export default App;
